import $ from 'jquery'
import proj4 from 'proj4'
import 'whatwg-fetch'
import 'es6-shim'
/**
 * @module
 * @api
 */

import Api from './Api'
import AutoSearch from './AutoSearch'
import AutoSelection from './AutoSelection'
import Snap from './Snap'
import Toc from './Toc'
import Toolbar from './Toolbar'
import Loader from './config/Loader'
import Parser from './config/Parser'
import ClientDom from './ui/ClientDom'
import loadTheme from './loadTheme'

import { getSelectStyleFunction, isInvisibleLayer } from './ol'
import drawLayer from './drawLayer'

import OLGA_VERSION from './version'
import {
  DoubleClickZoom,
  DragPan,
  PinchZoom,
  KeyboardPan,
  KeyboardZoom,
  MouseWheelZoom,
  DragZoom,
  Select
} from 'ol/interaction'
import Map from 'ol/Map'
import VectorTile from 'ol/layer/VectorTile'
import { register as setProj4 } from 'ol/proj/proj4'
import { Extent } from 'ol/extent'
import Applications from './applications/Applications'

proj4.defs([
  [
    'EPSG:3008',
    '+proj=tmerc +lat_0=0 +lon_0=13.5 +k=1 +x_0=150000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ],
  [
    'EPSG:3006',
    '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ],
  [
    'EPSG:3021',
    '+proj=tmerc +lat_0=0 +lon_0=15.80827777777778 +k=1 +x_0=1500000 +y_0=0 +ellps=bessel +units=m +no_defs'
  ],
  [
    'EPSG:2400',
    '+proj=tmerc +lat_0=0 +lon_0=15.80827777777778 +k=1 +x_0=1500000 +y_0=0 +ellps=bessel +towgs84=414.1,41.3,603.1,-0.855,2.141,-7.023,0 +units=m +no_defs'
  ]
])

setProj4(proj4)

/**
 * OLGA Client
 *
 * There are two ways to initialise the client with a configuration:
 *
 * 1. Use querystring parameter `config` to load configuration using AJAX
 * 1. Supply a config object to this constructor using the options argument
 *
 * If config is not specified the client will try to load a default config using AJAX.
 *
 * @property {object} options
 * @property {olga.config.Config=} options.config OLGA Configuration object (will override AJAX loaded config)
 * @property {olga.applications.Application[]} options.applications Register Olga applications
 * @property {function(Client)=} options.callback Called when the client is initialized and ready
 * @constructor
 * @api
 */
export default function Client(options) {
  options = options || {}

  $.extend(this, options)

  this.config = this.cloneConfig(this.config)

  this.registeredApplications = options.applications || []

  var loader = new Loader()
  loader.loadConfig(this.init.bind(this))
}

/**
 * Initialize client from config
 * @param {olga.config.Config} config
 */
Client.prototype.init = function(config) {
  this.config = $.extend({}, this.config, config)
  // bail if supplied/loaded config doesn't include
  if (!this.config.layers || !this.config.view) {
    return
  }

  const clientDom = new ClientDom({
    config: this.config
  })
  clientDom.render()
  if (
    this.config.navbars &&
    this.config.navbars.north &&
    this.config.navbars.north.disableButton
  ) {
    $('#xs-button').hide()
  }
  if (this.config.navbars && this.config.navbars.north.visible) {
  } else {
    $('nav.navbar-north').css('visibility', 'hidden')
    $('#map').css('top', '0px')
  }
  if (this.config.navbars && this.config.navbars.south.visible) {
  } else {
    $('nav.navbar-south').css('visibility', 'hidden')
    $('#map').css('bottom', '0px')
  }

  $('.navbar-toggle').on('click', e => {
    const selector = $(e.target)
      .closest('a')
      .attr('data-target')
    $(selector).toggleClass('in')
  })
  if (this.config.gps && !this.config.gps.visible) {
    $('.ol-gps').hide()
  }

  var parser = new Parser()

  var mapOptions = {}

  $.extend(mapOptions, this.config)
  $.extend(mapOptions, parser.parse(this.config), {
    target: 'map',
    pixelRatio: 1,
    moveTolerance: 1
  })

  mapOptions.interactions = [
    // new ol.interaction.DragRotate(),
    new DoubleClickZoom(),
    new DragPan(),
    // new ol.interaction.PinchRotate(),
    new PinchZoom(),
    new KeyboardPan(),
    new KeyboardZoom(),
    new MouseWheelZoom(),
    new DragZoom()
  ]

  this.map = new Map(mapOptions)

  loadTheme(this.config.theme, this.map)

  this.map.addLayer(drawLayer)

  this.drawLayer = drawLayer

  this.select = new Select({
    style: getSelectStyleFunction(),
    layers: function(layer) {
      if (layer instanceof VectorTile) return false

      var olgaProperties = layer.get('olga_')
      if (olgaProperties && olgaProperties.type === 'Text') {
        // olga.tool.Text handles text selection
        return false
      }

      if (
        (olgaProperties &&
          olgaProperties.metadata &&
          olgaProperties.metadata.select === false) ||
        isInvisibleLayer(layer)
      )
        return false

      return true
    },
    multi: true
    // condition: e =>
    //   !this.autoSelection.active && ol.events.condition.singleClick(e)
  })

  if (this.config.toc) {
    let toc = $('#layers-toggle-button')
    let tocLi = toc.closest('li')
    if (this.config.toc.position === 'north') {
      let tocClone = toc.clone()
      let northUL = $('#navbarNorthButtons')
      tocLi.remove()
      tocLi = $('<li></li>')
        .height('50px')
        .append(tocClone.css({ height: '100%', margin: '0 auto' }))
      northUL.append(tocLi)
    }
    if (typeof this.config.toc.visible === 'boolean') {
      if (!this.config.toc.visible) {
        tocLi.hide()
      }
    }
    this.layers = new Toc({
      config: this.config,
      map: this.map
    })
  } else {
    tocLi.hide()
  }

  this.applications = new Applications({
    applications: this.registeredApplications,
    client: this
  })

  /**
   * @type {module:Api}
   * @memberof! module:Client#
   * @api
   */
  this.api = new Api({
    config: this.config,
    map: this.map,
    toc: this.layers,
    applications: this.applications,
    client: this
  })

  this.toolbar = new Toolbar({
    config: this.config,
    map: this.map,
    select: this.select,
    api: this.api
  })

  this.autoSelection = new AutoSelection({
    map: this.map,
    toolbar: this.toolbar,
    api: {
      identify: this.api.identify
    },
    layers: this.config.layers,
    selectionStyle: this.config.selectionStyle,
    config: this.config
  })

  this.snap = new Snap({
    map: this.map,
    toolbar: this.toolbar,
    layers: this.config.layers
  })

  if (this.config.autoSearch) {
    $.extend(this.config.autoSearch, {
      map: this.map,
      api: this.api
    })
    this.autoSearch = new AutoSearch(this.config.autoSearch)
  }

  if (this.config.view.initialExtent) {
    this.zoomToExtent(this.config.view.initialExtent)
  }

  if (this.callback) this.callback(this)

  if (this.config.navbars.north.title || this.config.navbars.north.subtitle) {
    this.api.ui.setCustomHeader(
      this.config.navbars.north.title,
      this.config.navbars.north.subtitle
    )
  }
}

/**
 * OLGA Client version
 * @type {string}
 * @memberof module:Client
 * @api
 */
Client.version = OLGA_VERSION

/**
 * Select interaction
 *
 * Can be used to access currently selected features and more, see [Select](https://openlayers.org/en/v6.3.1/apidoc/module-ol_interaction_Select-Select.html).
 *
 * @type {Select}
 * @memberof! module:Client#
 * @api
 */
Client.prototype.select = null

/**
 * Clean up resources
 */
Client.prototype.dispose = function() {
  // goog.disposeAll(this.map, this.autoSearch)
}

/**
 * Zoom map to extent
 * @param {Extent} extent
 */
Client.prototype.zoomToExtent = function(extent) {
  this.map.getView().fit(extent, this.map.getSize())
}

Client.prototype.cloneConfig = function(config) {
  if (!config) {
    return {}
  }
  const configCopy = Object.assign({}, this.config)

  // Don't clone target wich can be a DOM-element
  delete this.config.target

  const nextConfig = JSON.parse(JSON.stringify(config))
  nextConfig.target = configCopy.target

  return nextConfig
}
